import * as React from "react"
import "./tutorial-dialog.css"
import classNames from "classnames"

/**
 * This defines a bezier curve, which is a shape with two control points & a smooth curved line between them
 */
export type BezierCurveDefinition = {
    C1: { x: number; y: number }
    C2: { x: number; y: number }
    end: { x: number; y: number }
}

// this is margin aronud the actual path that is important for the marker-end
const svgMargin = 10

const offsetX = ({ C1, C2, end }: BezierCurveDefinition) => Math.max(C1.x, C2.x, end.x, 0)
const offsetY = ({ C1, C2, end }: BezierCurveDefinition) => Math.max(C1.y, C2.y, end.y, 0)

const width = (offX: number, { C1, C2, end }: BezierCurveDefinition) =>
    offX - Math.min(C1.x, C2.x, end.x, 0)
const height = (offY: number, { C1, C2, end }: BezierCurveDefinition) =>
    offY - Math.min(C1.y, C2.y, end.y, 0)

const viewBox = (w: number, h: number, { C1, C2, end }: BezierCurveDefinition) =>
    [
        `${Math.min(C1.x, C2.x, end.x, 0) - svgMargin}`, // left
        `${Math.min(C1.y, C2.y, end.y, 0) - svgMargin}`, // top
        `${w + 2 * svgMargin}`, // width
        `${h + 2 * svgMargin}`, // height
    ].join(" ")

const pathDef = ({ C1, C2, end }: BezierCurveDefinition) =>
    [`M0 0`, `C`, `${C1.x} ${C1.y},`, `${C2.x} ${C2.y},`, `${end.x} ${end.y}`].join("")

export type TutorialDialogProps = {
    pointAt?: BezierCurveDefinition
    className?: string
} & React.PropsWithChildren

const TutorialDialogArrow = (pointAt: BezierCurveDefinition) => {
    const offX = offsetX(pointAt)
    const offY = offsetY(pointAt)
    const w = width(offX, pointAt)
    const h = height(offY, pointAt)
    return (
        <svg
            role="presentation"
            className="tutorial-dialog__arrow"
            width={w + 2 * svgMargin}
            height={h + 2 * svgMargin}
            style={{
                transform: `translate(-100%, -100%) translate(${
                    offX + 2 * svgMargin
                }px, ${offY}px)`,
            }}
            viewBox={viewBox(w, h, pointAt)}
        >
            <defs>
                <marker
                    id="tutorial-dialog__arrow-head"
                    viewBox="-10 -10 20 20"
                    markerWidth="20"
                    markerHeight="20"
                    orient="auto-start-reverse"
                    markerUnits="userSpaceOnUse"
                >
                    <path d="M-8 -8L0 0L-8 8" className="tutorial-dialog__arrow-path" />
                </marker>
            </defs>
            <path
                className="tutorial-dialog__arrow-path"
                d={pathDef(pointAt)}
                markerEnd="url(#tutorial-dialog__arrow-head)"
            />
        </svg>
    )
}

/**
 * @deprecated
 */
export const TutorialDialog = ({ pointAt, children, className }: TutorialDialogProps) => {
    return (
        <div className={classNames("tutorial-dialog", className)}>
            {pointAt && <TutorialDialogArrow {...pointAt} />}
            {children}
        </div>
    )
}
